/**
 * Dialog that shows the legal agreement PDF (Terms/Privacy) using PDF.js via react-pdf.
 * Accept button is disabled until the user has scrolled to the last page (scroll-to-bottom proof).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function LegalPdfDialog({ open, onClose, onAccept, pdfUrl, title = 'Terms of Service & Privacy Policy' }) {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [pageWidth, setPageWidth] = useState(null);
  const lastPageRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Fetch PDF when dialog opens so the request goes through the app origin (and dev proxy). Surface backend errors.
  useEffect(() => {
    if (!open || !pdfUrl) {
      setPdfFile(null);
      return;
    }
    setLoading(true);
    setError(null);
    setPdfFile(null);
    setNumPages(null);
    const ac = new AbortController();
    fetch(pdfUrl, { credentials: 'include', signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) {
          let msg = res.statusText;
          try {
            const data = await res.json();
            if (data?.detail) msg = data.detail;
          } catch {
            try { msg = await res.text() || msg; } catch { /* keep statusText */ }
          }
          setError(msg || `Failed to load PDF (${res.status})`);
          setLoading(false);
          return;
        }
        const blob = await res.blob();
        if (blob.type && !blob.type.includes('pdf')) {
          setError('Server did not return a PDF.');
          setLoading(false);
          return;
        }
        setPdfFile(blob);
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        setError(e?.message || 'Failed to fetch PDF. Check that the backend is running and the dev server proxy points to it.');
        setLoading(false);
      });
    return () => ac.abort();
  }, [open, pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setLoading(false);
    setError(null);
    setHasReachedEnd(false);
  }, []);

  const onDocumentLoadError = useCallback((e) => {
    setError(e?.message || 'Failed to load PDF');
    setLoading(false);
  }, []);

  // IntersectionObserver: when the last page enters the viewport, user has scrolled to bottom
  useEffect(() => {
    if (!open || numPages == null || !lastPageRef.current) return;
    const el = lastPageRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setHasReachedEnd(true);
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, numPages]);

  // Track available width so pages use most of the dialog horizontally
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (scrollContainerRef.current) {
        const w = scrollContainerRef.current.clientWidth;
        if (w) {
          // Leave a little breathing room for scrollbar and padding
          setPageWidth(w - 32);
        }
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  const handleAccept = () => {
    onAccept?.();
    onClose?.();
  };

  const handleClose = () => {
    setHasReachedEnd(false);
    setNumPages(null);
    setError(null);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '85vh', maxHeight: 800 }
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
          }}
        >
          {loading && (
            <Box sx={{ py: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading document…</Typography>
            </Box>
          )}
          {pdfFile && (
            <Document
              file={pdfFile}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              {numPages != null &&
                Array.from(new Array(numPages), (_, i) => (
                  <Box
                    key={`page_${i + 1}`}
                    ref={i + 1 === numPages ? lastPageRef : undefined}
                    sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}
                  >
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth || (typeof window !== 'undefined' ? Math.min(800, window.innerWidth - 96) : 600)}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  </Box>
                ))}
            </Document>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!hasReachedEnd || loading}
          color="primary"
        >
          {hasReachedEnd ? 'I have read and accept' : `Scroll to the end (${numPages ?? '…'} pages) to accept`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
